/**
 * Marple & Co. - Complete Product Catalogue 2024
 * All products, images, descriptions, and pricing from the official catalogue
 */

export interface Product {
  id: string;
  name: string;
  subtitle?: string;
  category: 'candles' | 'tea' | 'body-care' | 'skincare';
  subcategory?: string;
  description: string;
  longDescription?: string;
  price: number;
  originalPrice?: number;
  size: string;
  scent?: string;
  keyIngredients: string[];
  benefits: string[];
  howToUse?: string;
  rating: number;
  reviews: number;
  isNew?: boolean;
  isBestseller?: boolean;
  inStock: boolean;
  stockCount: number;
  images: string[];
  tags: string[];
  aiMatch?: number;
}

export const categories = [
  { id: 'all', label: 'All Products', color: '#061B37' },
  { id: 'candles', label: 'Gel Candles', color: '#42C5E7' },
  { id: 'tea', label: 'Herbal Teas', color: '#BDD630' },
  { id: 'body-care', label: 'Body Care', color: '#F4C95D' },
  { id: 'skincare', label: 'Skin Care', color: '#E07A5F' },
];

export const products: Product[] = [
  // ==================== GEL CANDLES ====================
  {
    id: 'aqua-gel-candle',
    name: 'Aqua Gel Candle',
    subtitle: 'Ocean Escape',
    category: 'candles',
    subcategory: 'Aromatherapy',
    description: 'Ocean Escape in a Candle. With real sand & seashells, brings the sea\'s calm to your home.',
    longDescription: 'Ocean Escape in a Candle. Marple & Co. Aqua Gel Candle, with real sand & seashells, brings the sea\'s calm to your home. Minty, fresh, woody notes recreate the ocean breeze. Handmade serenity that promotes emotional balance and soothes your senses. Each candle is carefully crafted with premium gel wax and genuine ocean elements.',
    price: 544.29,
    size: '250ml',
    scent: 'Ocean Breeze',
    keyIngredients: ['Gel Wax', 'Real Sand', 'Seashells', 'Essential Oils'],
    benefits: [
      'Promotes emotional balance',
      'Soothes your senses',
      'Creates calming atmosphere',
      'Long-lasting burn (40-45 hours)'
    ],
    howToUse: 'Trim wick to 5mm before lighting. Allow wax to pool to edges on first burn. Never leave burning candle unattended.',
    rating: 4.9,
    reviews: 234,
    isBestseller: true,
    inStock: true,
    stockCount: 45,
    images: [
      '/products/aqua-candle-1.jpg',
      '/products/aqua-candle-2.jpg',
      '/products/candles-duo.jpg'
    ],
    tags: ['candle', 'aromatherapy', 'ocean', 'relaxation', 'home'],
    aiMatch: 96
  },
  {
    id: 'ignite-gel-candle',
    name: 'Ignite Gel Candle',
    subtitle: 'Rose & Romance',
    category: 'candles',
    subcategory: 'Aromatherapy',
    description: 'Handcrafted with meticulous care, featuring clear gel, delicate rose petals, and soothing sea salt.',
    longDescription: 'Discover the mesmerizing allure of our Ignite Gel Candle. Handcrafted with meticulous care, this unique candle features a captivating blend of clear gel, delicate rose petals, and soothing sea salt. As the flame dances, the subtle glow illuminates the natural beauty within, while the enchanting rose geranium scent delicately unfurls, creating an ambiance of serenity and warmth. Let the gentle fragrance, infused with the essence of African botanicals, envelop you, transforming any space into a sanctuary of peace.',
    price: 544.29,
    size: '250ml',
    scent: 'Rose Geranium',
    keyIngredients: ['Clear Gel Wax', 'Rose Petals', 'Sea Salt', 'Rose Geranium Essential Oil'],
    benefits: [
      'Creates romantic ambiance',
      'Soothes and calms mind',
      'Natural air freshener',
      'Elegant home décor piece'
    ],
    howToUse: 'Place on heat-resistant surface. Trim wick before each use. Burn for 2-3 hours at a time for optimal performance.',
    rating: 4.8,
    reviews: 189,
    isNew: true,
    inStock: true,
    stockCount: 38,
    images: [
      '/products/ignite-candle-1.jpg',
      '/products/ignite-candle-2.jpg',
      '/products/candles-duo.jpg'
    ],
    tags: ['candle', 'aromatherapy', 'rose', 'romance', 'luxury'],
    aiMatch: 94
  },

  // ==================== HERBAL TEAS ====================
  {
    id: 'morning-herbal-tea',
    name: 'Herbal Blend Morning Tea',
    subtitle: 'Good Morning',
    category: 'tea',
    subcategory: 'African Blends',
    description: 'Awaken your radiance with our Morning Tea. A blend of African Potato, Geranium, and Ashwagandha.',
    longDescription: 'Awaken your radiance with our Morning Tea. Inspired by ancient African traditions, this exquisite blend of herbs offers a holistic approach to beauty and well-being. Ashwagandha, known as "Indian Ginseng," combats stress and promotes calm, balanced skin. Soothing Geranium minimizes redness and irritation, while antimicrobial and antioxidant-rich African Potato protects against environmental damage. Rich in antioxidants and circulation-boosting compounds.',
    price: 144.86,
    size: '60g (15 Tea Bags)',
    scent: 'Botanical',
    keyIngredients: ['African Potato', 'Geranium', 'Ashwagandha', 'Rooibos'],
    benefits: [
      'Combats stress and promotes calm',
      'Minimizes redness and irritation',
      'Protects against environmental damage',
      'Boosts morning energy naturally'
    ],
    howToUse: 'Steep 1 tea bag in hot water for 5-7 minutes. Enjoy in the morning for best results. Can be enjoyed with honey.',
    rating: 4.7,
    reviews: 156,
    isNew: false,
    inStock: true,
    stockCount: 89,
    images: [
      '/products/morning-tea-1.jpg',
      '/products/morning-tea-2.jpg',
      '/products/tea-range.jpg'
    ],
    tags: ['tea', 'morning', 'wellness', 'african-botanicals', 'organic'],
    aiMatch: 92
  },
  {
    id: 'evening-herbal-tea',
    name: 'Herbal Blend Evening Tea',
    subtitle: 'Good Evening',
    category: 'tea',
    subcategory: 'African Blends',
    description: 'Indulge in tranquility with our Evening Herbal Tea. A blend of African Potato, Patchouli, and Hibiscus.',
    longDescription: 'Indulge in a moment of tranquility with our Evening Herbal Tea. This soothing blend, inspired by the rich heritage of African botanicals, is carefully crafted to help you unwind and prepare for a restful night\'s sleep while supporting your skin\'s health. African Potato provides protective antioxidants, Patchouli promotes relaxation and skin regeneration, while Hibiscus delivers natural vitamin C for glowing skin from within.',
    price: 144.86,
    size: '60g (15 Tea Bags)',
    scent: 'Floral Earthy',
    keyIngredients: ['African Potato', 'Patchouli', 'Hibiscus', 'Chamomile'],
    benefits: [
      'Promotes restful sleep',
      'Supports skin regeneration',
      'Rich in natural Vitamin C',
      'Calms mind and body'
    ],
    howToUse: 'Steep 1 tea bag in hot water for 7-10 minutes before bedtime. Enjoy the calming aroma as you unwind.',
    rating: 4.8,
    reviews: 203,
    isBestseller: true,
    inStock: true,
    stockCount: 67,
    images: [
      '/products/evening-tea-1.jpg',
      '/products/evening-tea-2.jpg',
      '/products/tea-range.jpg'
    ],
    tags: ['tea', 'evening', 'sleep', 'relaxation', 'african-botanicals'],
    aiMatch: 95
  },

  // ==================== BODY CARE - SPATIQUE RANGE ====================
  {
    id: 'spatique-soak',
    name: 'Spatique Soak',
    subtitle: 'Bath Salts',
    category: 'body-care',
    subcategory: 'Spatique',
    description: 'Escape to tranquility with our Spatique Bath Soak. A soothing blend of African botanicals and essential oils.',
    longDescription: 'Escape to tranquility with our Spatique Bath Soak. Immerse yourself in a soothing blend of African botanicals and essential oils, carefully curated to melt away stress and leave your skin feeling incredibly soft. Let the calming aromas envelop you as you unwind, transforming your bath into a sanctuary of serenity. Features Kalahari Melon for hydration, African Thyme for purification, and Rose Geranium for emotional balance.',
    price: 135.08,
    size: '400g',
    scent: 'Rose Geranium',
    keyIngredients: ['Kalahari Melon', 'African Thyme', 'Rose Geranium', 'Epsom Salt', 'Dead Sea Salt'],
    benefits: [
      'Melts away stress and tension',
      'Leaves skin incredibly soft',
      'Detoxifies and purifies',
      'Creates spa-like experience'
    ],
    howToUse: 'Add 2-3 handfuls to warm running bath water. Soak for 20-30 minutes. Rinse skin after bath.',
    rating: 4.9,
    reviews: 312,
    isBestseller: true,
    inStock: true,
    stockCount: 54,
    images: [
      '/products/spatique-soak-1.jpg',
      '/products/spatique-soak-2.jpg'
    ],
    tags: ['bath', 'soak', 'relaxation', 'detox', 'spa'],
    aiMatch: 97
  },
  {
    id: 'spatique-body-scrub',
    name: 'Spatique Body Scrub',
    subtitle: 'Sugar Scrub',
    category: 'body-care',
    subcategory: 'Spatique',
    description: 'Unveil your skin\'s radiant best with our luxurious sugar body scrub ritual.',
    longDescription: 'Unveil your skin\'s radiant best with our Spatique Sugar Body Scrub. More than just an exfoliant, this is a luxurious ritual, a moment of self-care that leaves your skin feeling incredibly soft, improved complexion, firm and revitalized. The natural sugar crystals gently buff away dead skin cells while nourishing oils leave your skin silky smooth and hydrated.',
    price: 202.21,
    size: '250ml',
    scent: 'Citrus Botanical',
    keyIngredients: ['Natural Sugar Crystals', 'Coconut Oil', 'Vitamin E', 'Citrus Essential Oils'],
    benefits: [
      'Gently exfoliates dead skin cells',
      'Improves skin complexion',
      'Leaves skin firm and revitalized',
      'Deeply hydrates and nourishes'
    ],
    howToUse: 'Apply to damp skin in circular motions. Focus on rough areas. Rinse thoroughly. Use 2-3 times per week.',
    rating: 4.8,
    reviews: 278,
    isNew: false,
    inStock: true,
    stockCount: 43,
    images: [
      '/products/body-scrub-1.jpg',
      '/products/body-scrub-2.jpg'
    ],
    tags: ['scrub', 'exfoliate', 'body', 'smooth-skin', 'spa'],
    aiMatch: 93
  },
  {
    id: 'hydrating-mousse-oil',
    name: 'Hydrating Mousse Oil',
    subtitle: 'Body Mousse',
    category: 'body-care',
    subcategory: 'Spatique',
    description: 'Pamper your skin with our luxurious whipped blend of African botanicals.',
    longDescription: 'Pamper your skin with our luxurious Mousse Oil, a whipped blend of African botanicals. This nourishing oil melts into the skin, restoring its radiance. Key ingredients include Shea Butter for hydration, Kalahari Melon seed oil for elasticity, Bee Propolis for protection and healing, and Geranium for soothing. Ideal for dry, sensitive skin and minimizing stretch marks.',
    price: 210.72,
    size: '250ml',
    scent: 'Floral Honey',
    keyIngredients: ['Shea Butter', 'Kalahari Melon Seed Oil', 'Bee Propolis', 'Geranium', 'Vitamin E'],
    benefits: [
      'Intensely hydrates dry skin',
      'Improves skin elasticity',
      'Helps minimize stretch marks',
      'Protects and heals skin barrier'
    ],
    howToUse: 'Apply to damp skin after bathing. Massage until fully absorbed. Focus on dry areas and stretch marks.',
    rating: 4.9,
    reviews: 345,
    isBestseller: true,
    inStock: true,
    stockCount: 38,
    images: [
      '/products/mousse-oil-1.jpg',
      '/products/mousse-oil-2.jpg'
    ],
    tags: ['body-oil', 'hydration', 'stretch-marks', 'dry-skin', 'mousse'],
    aiMatch: 96
  },
  {
    id: 'spatique-souffle',
    name: 'Spatique Soufflé',
    subtitle: 'Body Butter',
    category: 'body-care',
    subcategory: 'Spatique',
    description: 'Natural, rich butter formula that melts into your skin, leaving it feeling incredibly soft and deeply nourished.',
    longDescription: 'This natural, rich butter formula, free of parabens, petroleum, and sulfates, melts into your skin, leaving it feeling incredibly soft, supple, and deeply nourished. We\'ve harnessed the potent power of African botanicals, blending the remarkable rejuvenating properties of African Potato with the deeply hydrating and skin-softening Ximenia Oil and the moisture-locking magic of Hyaluronic Acid.',
    price: 194.06,
    size: '250ml',
    scent: 'Vanilla Botanical',
    keyIngredients: ['African Potato', 'Ximenia Oil', 'Hyaluronic Acid', 'Shea Butter', 'Cocoa Butter'],
    benefits: [
      'Deeply nourishes and hydrates',
      'Improves skin suppleness',
      'Locks in moisture for 24 hours',
      'Free from harmful chemicals'
    ],
    howToUse: 'Apply generously all over body. Best used after bathing on slightly damp skin. Allow to absorb before dressing.',
    rating: 4.7,
    reviews: 198,
    isNew: true,
    inStock: true,
    stockCount: 52,
    images: [
      '/products/souffle-1.jpg',
      '/products/souffle-2.jpg'
    ],
    tags: ['body-butter', 'nourishing', 'natural', 'african-botanicals', 'hydration'],
    aiMatch: 91
  }
];

// Helper functions
export const getProductById = (id: string): Product | undefined => {
  return products.find(p => p.id === id);
};

export const getProductsByCategory = (category: string): Product[] => {
  if (category === 'all') return products;
  return products.filter(p => p.category === category);
};

export const getBestsellers = (): Product[] => {
  return products.filter(p => p.isBestseller);
};

export const getNewArrivals = (): Product[] => {
  return products.filter(p => p.isNew);
};

export const getRelatedProducts = (product: Product): Product[] => {
  return products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);
};
